import { AppSettings } from "../../app.settings";
import { UserDbRole } from "../broker/domain/models/broker.model";

// TODO - replace with projects\partner\src\app\core\const\api-paths.ts
export const ApiUrls = (appSettings: AppSettings) => {
  return {
    GetCurrentUserDetails: `${appSettings.brokers.url}/api/user/current`,
    GetBrokerDetails: ({ user }: { user: { brokerId: number } }) =>
      `${appSettings.brokers.url}/api/broker/${user.brokerId}`,
    GetBrokerUsers: `${appSettings.brokers.url}/api/user`,
    GetBrokerUser: ({ user }: { user: { id: number } }) =>
      `${appSettings.brokers.url}/api/user/${user.id}`,
    CreateBrokerUser: `${appSettings.brokers.url}/api/user/team-invitation`,
    EditBrokerUser: ({ user }: { user: { id: number } }) =>
      `${appSettings.brokers.url}/api/user/${user.id}`,
    DeleteBrokerUser: ({ user }: { user: { id: number } }) =>
      `${appSettings.brokers.url}/api/user/${user.id}`,
    UpdatePassword: ({ user }: { user: { id: number } }) =>
      `${appSettings.brokers.url}/api/user/${user.id}/password`,
    GetLostReasons: () =>
      `${appSettings.applications.url}/api/dictionary/lost-reason`,
    GetCompetitors: () =>
      `${appSettings.competitors.url}/competitors`,
    SendBankLinkEmail: ({ application }: { application: { id: number } }) =>
      `${appSettings.banking.url}/api/banking/linking-invitation/${application.id}`,
    DashboardWidgets: `${appSettings.brokers.url}/api/dashboard`,
    ResendInvitation: (brokerUserId: number) => `${appSettings.brokers.url}/api/user/resend-invitation/${brokerUserId}`,
    ResetAuthenticatorApp: (brokerUserId: number) => `${appSettings.brokers.url}/api/user/${brokerUserId}/reset-authenticator-app`
  };
};

export const WebPaths = {
  Dashboard: "/dashboard",
  DashboardPage: (slug: string): string => `/dashboard/${slug}`,
  SignIn: "/sign-in",
  SignUp: "/sign-up",
  Profile: "/profile",
  Mfa: "/mfa",
  UpdatePassword: "/update-password",
  ResetPassword: "/password/reset",
  ResetPasswordDone: "/reset-done",
  Submissions: "/submissions",
  NewSubmission: "/submission/new",
  Team: "/team",
  NewUser: "/user/new",
  EditUser: ({ user }: { user: { id: number } }) => `/user/${user.id}/edit`,
};

const excludedLoginRedirectPaths = ["/password/reset", "/sign-in"];

function stripSlashes(str: string): string {
  return str.replace(/\/\\/i, "");
}

function isPathInPaths(path: string, paths: string[]) {
  path = stripSlashes(path);
  return paths.some(pathStart => {
    return path.startsWith(stripSlashes(pathStart));
  });
}

export function isExcludedLoginRedirectPath(path: string): boolean {
  return isPathInPaths(path, excludedLoginRedirectPaths);
}

export enum UserUiRole {
  ADMIN = "Admin",
  USER = "Team Member",
  DEVELOPER = "Developer",
}

// map user's role from db to UI friendly name
export const userRoleUiMap = new Map<UserDbRole, UserUiRole>([
  [UserDbRole.ADMIN, UserUiRole.ADMIN],
  [UserDbRole.USER, UserUiRole.USER],
  [UserDbRole.DEVELOPER, UserUiRole.DEVELOPER]
]);

export enum AdminSidenavFilters {
  ADMIN = "Admin",
  ApiKeys = "API Keys",
  ApiDocs = "API Docs",
}

export enum TeamSidenavFilters {
  TEAM = "Team",
  ADMINS = "Admins",
  TEAM_MEMBERS = "Team Members",
  DEACTIVATED_USERS = "Deactivated Users",
  DEVELOPERS = "Developers",
}

export enum BrokerStatus {
  DRAFT = "draft",
  SUBMISSION_INCOMPLETE = "submission-incomplete",
  PROCESSING = "processing",
  OFFER = "offer",
  CONDITIONAL_OFFER = "conditional-offer",
  CLOSING = "closing",
  CLOSING_INCOMPLETE = "closing-incomplete",
  CONTRACT_READY = "contract-ready",
  CONTRACT_OUT = "contract-out",
  DECLINED = "declined",
  DORMANT = "dormant",
  ABANDONED = "abandoned",
  OPEN = "open",
  FUNDED = "funded",
  RENEWED = "renewed",
  CLOSED = "closed",
  NOT_INTERESTED = "not-interested",
}

export const BrokerStatusLabel = new Map<string, string>([
  [BrokerStatus.DRAFT, 'Draft'],
  [BrokerStatus.SUBMISSION_INCOMPLETE, 'Submission incomplete'],
  [BrokerStatus.PROCESSING, 'Processing'],
  [BrokerStatus.OFFER, 'Offer'],
  [BrokerStatus.CONDITIONAL_OFFER, 'Conditional Offer'],
  [BrokerStatus.CLOSING, 'Closing'],
  [BrokerStatus.CLOSING_INCOMPLETE, 'Closing incomplete'],
  [BrokerStatus.CONTRACT_READY, 'Contract Ready'],
  [BrokerStatus.CONTRACT_OUT, 'Contract Out'],
  [BrokerStatus.DECLINED, 'Declined'],
  [BrokerStatus.DORMANT, 'Dormant'],
  [BrokerStatus.ABANDONED, 'Abandoned'],
  [BrokerStatus.OPEN, 'Open'],
  [BrokerStatus.FUNDED, 'Funded'],
  [BrokerStatus.RENEWED, 'Renewed'],
  [BrokerStatus.CLOSED, 'Closed'],
  [BrokerStatus.NOT_INTERESTED, 'Not Interested'],
]);

export const passwordValidatorRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

export const DEFAULT_UPPY_FILE_DIALOG_CLASS = "uppy-dialog";
